import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { getRuntimeErrorMessage, logRuntimeIssue } from '@/utils/runtimeDiagnostics';

interface RouteErrorBoundaryProps {
  children: ReactNode;
  routePath: string;
}

interface RouteErrorBoundaryState {
  error: Error | null;
}

function isChunkLoadError(error: unknown): boolean {
  const message = getRuntimeErrorMessage(error).toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('dynamically imported module') ||
    message.includes('chunkloaderror') ||
    message.includes('loading chunk') ||
    message.includes('importing a module script failed') ||
    message.includes('module script')
  );
}

class RouteErrorBoundaryInner extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logRuntimeIssue('route-error-boundary', {
      routePath: this.props.routePath,
      error,
      endpoint: isChunkLoadError(error) ? 'route-lazy-import-chunk' : 'route-render-or-lazy-import',
      statusCode: null,
    });
    console.warn('[runtime:route-error-boundary:componentStack]', info.componentStack);
  }

  componentDidUpdate(prevProps: RouteErrorBoundaryProps): void {
    if (prevProps.routePath !== this.props.routePath && this.state.error) {
      this.setState({ error: null });
    }
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    const chunkLoadFailed = isChunkLoadError(this.state.error);
    const title = chunkLoadFailed ? '頁面資源載入失敗，請重新整理' : '頁面載入失敗';
    const description = chunkLoadFailed
      ? '可能是新版部署後瀏覽器仍使用舊版頁面資源，請重新整理取得最新版本。'
      : '請重新載入，或回到上一頁再試一次。';

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <div className="w-full max-w-md bg-white border border-gray-200 rounded-xl shadow-sm p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{title}</h1>
          <p className="text-sm text-gray-600 mb-2">{description}</p>
          <p className="text-xs text-gray-400 break-words mb-6">
            {getRuntimeErrorMessage(this.state.error)}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800"
            >
              重新載入
            </button>
            <button
              type="button"
              onClick={() => window.history.back()}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              返回上一頁
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  const routePath = `${location.pathname}${location.search}`;

  return (
    <RouteErrorBoundaryInner key={location.pathname} routePath={routePath}>
      {children}
    </RouteErrorBoundaryInner>
  );
}

export function RouteLoadingFallback() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-sm text-gray-600">頁面載入中...</p>
      </div>
    </div>
  );
}